/**
 * @internal
 */
import { useCallback, useRef, useState } from 'react';
import { useUpdateEffect } from '@polestar/component-warehouse-react/hooks/useUpdateEffect';
/**
 * Used when a component has a more complex setup to determine when the focus / blur
 * events are triggered.
 *
 * `useFocusTriggers` is meant for atoms and `useContainerFocusTriggers` for molecules or organisms
 */
export const useFocusTriggers = ({ name, fakeFocus = false, onBlur, onFocus, }) => {
    const [focused, setFocused] = useState(false);
    const internalFocused = focused || fakeFocus;
    /**
     *  We need to ref these functions otherwise the onblur/onfocus will trigger when the function change.
     *  We had it in a useEffect before, but then that might trigger AFTER the useUpdateEffect below.
     *  So we just update the ref in the loop itself.
     */
    const onFocusRef = useRef(onFocus);
    onFocusRef.current = onFocus;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;
    /**
     * Fake the blur / focus with an effect
     */
    useUpdateEffect(() => {
        var _a;
        if (!internalFocused) {
            (_a = onBlurRef.current) === null || _a === void 0 ? void 0 : _a.call(onBlurRef, name);
        }
    }, [internalFocused]);
    useUpdateEffect(() => {
        var _a;
        if (internalFocused) {
            (_a = onFocusRef.current) === null || _a === void 0 ? void 0 : _a.call(onFocusRef, name);
        }
    }, [internalFocused]);
    /**
     * These are the events that get put on the element
     */
    const handleBlur = useCallback(() => {
        setFocused(false);
    }, []);
    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);
    const focusBlurEvents = {
        onBlur: handleBlur,
        onFocus: handleFocus,
    };
    return {
        realFocus: focused,
        focused: internalFocused,
        setFocused,
        handleBlur,
        handleFocus,
        focusBlurEvents,
    };
};
